import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import { MenuItem, Select, Stack, SxProps, Theme, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import { getObjectTypeOptions } from "@/helpers/realEstateParametersHelpers";
import { ObjectTypeEnum } from "@/types/refineRealEstate";

interface ObjectCategoryDropdownProps {
  value: ObjectTypeEnum | null | "";
  onChange: (value: ObjectTypeEnum | null) => void;
  selectSx?: SxProps<Theme>;
}

export const ObjectCategoryDropdown: React.FC<ObjectCategoryDropdownProps> = ({ value, onChange, selectSx }) => {
  const { t } = useTranslation("ValuationListPage");
  const { t: tRealEstate } = useTranslation("Common", { keyPrefix: "realEstate" });
  const theme = useTheme();

  const objectTypeOptions: Array<{ value: ObjectTypeEnum | ""; label: string; disabled?: boolean }> = [
    { value: "", label: t("all") },
    ...getObjectTypeOptions(tRealEstate),
  ];

  const internalValue: ObjectTypeEnum | "" = (value ?? "") as ObjectTypeEnum | "";

  const selectWidth =
    typeof selectSx === "object" && selectSx && "width" in selectSx
      ? (selectSx.width as string | number | undefined)
      : undefined;

  return (
    <Stack spacing={2}>
      <Typography variant="subText">{t("category")}</Typography>
      <Select
        value={internalValue}
        onChange={(event) => {
          const next = event.target.value as ObjectTypeEnum | "";
          onChange(next === "" ? null : (next as ObjectTypeEnum));
        }}
        displayEmpty
        sx={selectSx}
        MenuProps={{
          PaperProps: {
            sx: {
              width: "fit-content",
              minWidth: "100%",
              maxWidth: selectWidth,
            },
          },
        }}
        IconComponent={(props) => (
          <ExpandMoreOutlinedIcon
            {...props}
            sx={{
              ...props.sx,
              "&, & path": {
                color: `${theme.palette.primary.main} !important`,
                fill: `${theme.palette.primary.main} !important`,
              },
            }}
          />
        )}
        data-cy="object-category-filter"
      >
        {objectTypeOptions.map((opt) => (
          <MenuItem
            key={opt.value ?? "all"}
            value={opt.value}
            disabled={opt.disabled}
            sx={{
              whiteSpace: "normal",
              wordWrap: "break-word",
            }}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </Stack>
  );
};
